import { Injectable } from '@angular/core';

const INSTALL_KEY = 'polilo.device.installId';
const CLIENT_ID_KEY = 'polilo.device.clientId';

/**
 * Builds a stable web client identifier for login audit.
 *
 * Algorithm:
 * 1. Persist a random install UUID in localStorage (survives reloads).
 * 2. Collect a browser fingerprint (UA, language, timezone, screen, cores).
 * 3. SHA-256(`web|{installId}|{fingerprint}`) → first 32 hex chars.
 * 4. Final id: `web_{hash}` (also cached in localStorage).
 *
 * Clearing site data rotates the install seed and therefore the clientId.
 */
@Injectable({ providedIn: 'root' })
export class ClientDeviceIdService {
  private cached: string | null = null;
  private inflight: Promise<string> | null = null;

  /** Resolve the current web client id (async because of SubtleCrypto). */
  resolve(): Promise<string> {
    if (this.cached) {
      return Promise.resolve(this.cached);
    }
    if (typeof localStorage !== 'undefined') {
      const stored = localStorage.getItem(CLIENT_ID_KEY);
      if (stored) {
        this.cached = stored;
        return Promise.resolve(stored);
      }
    }
    if (!this.inflight) {
      this.inflight = this.compute()
        .then((id) => {
          this.cached = id;
          try {
            localStorage.setItem(CLIENT_ID_KEY, id);
          } catch {
            /* private mode / quota — still return the computed id */
          }
          return id;
        })
        .finally(() => {
          this.inflight = null;
        });
    }
    return this.inflight;
  }

  private async compute(): Promise<string> {
    const installId = this.installId();
    const fingerprint = this.fingerprintParts().join('|');
    const material = `web|${installId}|${fingerprint}`;
    const hash = await sha256Hex(material);
    return `web_${hash.slice(0, 32)}`;
  }

  private installId(): string {
    try {
      const existing = localStorage.getItem(INSTALL_KEY);
      if (existing) {
        return existing;
      }
      const created = crypto.randomUUID();
      localStorage.setItem(INSTALL_KEY, created);
      return created;
    } catch {
      return crypto.randomUUID();
    }
  }

  private fingerprintParts(): string[] {
    if (typeof navigator === 'undefined' || typeof screen === 'undefined') {
      return ['ssr'];
    }
    return [
      navigator.userAgent || '',
      navigator.language || '',
      Array.isArray(navigator.languages) ? navigator.languages.join(',') : '',
      Intl.DateTimeFormat().resolvedOptions().timeZone || '',
      String(screen.width),
      String(screen.height),
      String(screen.colorDepth),
      String(navigator.hardwareConcurrency ?? ''),
      navigator.platform || '',
    ];
  }
}

async function sha256Hex(value: string): Promise<string> {
  if (typeof crypto !== 'undefined' && crypto.subtle) {
    const data = new TextEncoder().encode(value);
    const digest = await crypto.subtle.digest('SHA-256', data);
    return [...new Uint8Array(digest)].map((b) => b.toString(16).padStart(2, '0')).join('');
  }
  // Non-crypto fallback (tests / odd runtimes) — still deterministic.
  return fnv1aHex(value);
}

/** 64-bit FNV-1a hex (not cryptographic; last-resort fallback). */
function fnv1aHex(value: string): string {
  let hash = 0xcbf29ce484222325n;
  const prime = 0x100000001b3n;
  for (let i = 0; i < value.length; i++) {
    hash ^= BigInt(value.charCodeAt(i));
    hash = (hash * prime) & 0xffffffffffffffffn;
  }
  return hash.toString(16).padStart(16, '0').repeat(2).slice(0, 64);
}
